// src/nav/NavigationBar.tsx

import React, {
    useContext,
    useState,
    useEffect,
    CSSProperties,
} from 'react';
import { ThemeContext } from '../context/ThemeContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faHouse,
    faStore,
    faCoins,
} from '@fortawesome/free-solid-svg-icons';
import { useNavigate, useLocation } from 'react-router-dom';

// ✅ 앱 전체에서 사용할 기준 해상도 (갤럭시/아이폰 세로)
export const APP_BASE_WIDTH = 350;
export const APP_BASE_HEIGHT = 650;

// ✅ 현재 윈도우 기준 스케일 계산 함수
const calcScale = () => {
    if (typeof window === 'undefined') return 1;
    const vw = window.innerWidth;
    const vh = window.innerHeight;

    const widthRatio = vw / APP_BASE_WIDTH;
    const heightRatio = vh / APP_BASE_HEIGHT;
    const ratio = Math.min(widthRatio, heightRatio);

    // 스마트폰 기준(1배)보다 작게는 줄이지 않고, 큰 화면에서만 커지도록
    return ratio < 1 ? 1 : ratio;
};

const NavigationBar: React.FC = () => {
    const { theme } = useContext(ThemeContext);
    const navigate = useNavigate();
    const location = useLocation();

    const [hoverPath, setHoverPath] = useState<string | null>(null);

    // ✅ 처음 렌더할 때도 즉시 스케일 계산해서 깜빡임 방지
    const [scale, setScale] = useState<number>(() => calcScale());

    const inactiveColor = '#8b8f9c';
    const activeColor = '#ffffff';

    // ✅ 리사이즈 될 때만 스케일 재계산
    useEffect(() => {
        const handleResize = () => {
            setScale(calcScale());
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const styles = {
        footer: {
            position: 'fixed',
            bottom: 0,
            left: '50%',
            transform: `translateX(-50%) scale(${scale})`, // ✅ 가운데 정렬 + 스케일
            transformOrigin: 'bottom center',              // ✅ 아래 중앙 기준으로 확대
            width: '100%',
            maxWidth: APP_BASE_WIDTH,
            margin: '0 auto',
            padding: '0 5px 12px',
            boxSizing: 'border-box' as const,
            zIndex: 1000,
        } as CSSProperties,
        bar: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            backgroundColor: theme.background_02,
            borderRadius: '10px',
            padding: '4px 10px',
            boxShadow: '0 -6px 24px rgba(0, 0, 0, 0.55)',
        } as CSSProperties,
        item: {
            flex: 1,
            display: 'flex',
            flexDirection: 'column' as const,
            alignItems: 'center',
            justifyContent: 'center',
            height: '54px',
            cursor: 'pointer',
            position: 'relative' as const,
            borderRadius: '16px',
            padding: '0 4px',
            backgroundColor: 'transparent',
        } as CSSProperties,
        iconWrap: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginBottom: '2px',
        } as CSSProperties,
        label: {
            fontSize: '10px',
            lineHeight: 1.2,
            transition: 'color 0.12s ease-out, font-weight 0.12s ease-out',
        } as CSSProperties,
    };

    const items = [
        { icon: faHouse, label: '홈', path: '/BalanceScreen' },
        { icon: faCoins, label: '결제', path: '/BuyerScreen' },
        { icon: faStore, label: '판매', path: '/SellerScreen' },
    ];

    const isActive = (path: string) => location.pathname === path;
    const isHover = (path: string) => hoverPath === path;

    const getColor = (path: string) => {
        if (isActive(path) || isHover(path)) return activeColor;
        return inactiveColor;
    };

    const getLabelStyle = (path: string): CSSProperties => ({
        ...styles.label,
        color: getColor(path),
        fontWeight: isActive(path) ? 600 : 400,
    });

    return (
        <div style={styles.footer}>
            <div style={styles.bar}>
                {items.map((item) => (
                    <div
                        key={item.path}
                        style={styles.item}
                        onClick={() => {
                            if (!isActive(item.path)) {
                                navigate(item.path);
                            }
                        }}
                        onMouseEnter={() => setHoverPath(item.path)}
                        onMouseLeave={() => setHoverPath(null)}
                    >
                        <div style={styles.iconWrap}>
                            <FontAwesomeIcon
                                icon={item.icon}
                                fontSize={22}
                                color={getColor(item.path)}
                            />
                        </div>
                        <div style={getLabelStyle(item.path)}>{item.label}</div>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default NavigationBar;
